import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback, useRef } from "react";
import { useAccount, useDisconnect, useSignMessage } from "wagmi";
import { useAppKit, useAppKitAccount } from "@reown/appkit/react";
import { WalletState } from "../types";
import { useAlert } from "../hooks/useAlert";
import AlertPopup from "../components/AlertPopup";
import { SourceTracker } from "../utils/sourceTracking";
import { API_BASE_URL, ApiService } from "../services/api";
import { getStoredReferrerCode, clearStoredReferrerCode } from "../utils/referral";

// Enhanced AuthState for AppKit integration
interface AppKitAuthState {
  isAuthenticated: boolean;
  token: string | null;
  walletAddress: string | null;
}

// User information from /auth/me endpoint
interface UserInfo {
  walletAddress: string;
  isAdmin: boolean;
  createdAt?: string;
  welcomeAirdropEligible: boolean;
  welcomeAirdropParticipated: boolean;
}

interface AppKitAuthContextType extends AppKitAuthState {
  login: () => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
  walletState: WalletState;
  updateBalance: () => Promise<void>;
  isEmbeddedWallet: boolean;
  isInitializing: boolean;
  // User information
  userInfo: UserInfo | null;
  refreshUserInfo: () => Promise<void>;
  // Account management methods
  openAccount: () => void;
  openOnRamp: () => void;
  openSwap: () => void;
}

const AppKitAuthContext = createContext<AppKitAuthContextType | undefined>(undefined);

interface AppKitAuthProviderProps {
  children: ReactNode;
}

export const AppKitAuthProvider: React.FC<AppKitAuthProviderProps> = ({ children }) => {
  // AppKit/Wagmi hooks with error handling
  const { open } = useAppKit();
  const { address, isConnected, chainId } = useAccount();
  const { embeddedWalletInfo } = useAppKitAccount();
  const { disconnect } = useDisconnect();
  const { signMessageAsync: originalSignMessageAsync } = useSignMessage();

  // Wrap signMessageAsync with logging to track all calls
  const signMessageAsync = useCallback(
    async (args: any) => {
      const signCallId = Math.random().toString(36).substr(2, 9);
      console.log(`🔏 [SIGN-${signCallId}] signMessageAsync called directly (not through our wrapper)`, {
        timestamp: Date.now(),
        message: args.message?.slice(0, 50) + "...",
        authInProgress: authInProgressRef.current,
        stackTrace: new Error().stack?.split("\n").slice(1, 5).join("\n"),
      });

      try {
        const result = await originalSignMessageAsync(args);
        console.log(`🔏 [SIGN-${signCallId}] signMessageAsync completed`, {
          timestamp: Date.now(),
          signatureLength: result?.length,
        });
        return result;
      } catch (error) {
        console.error(`🔏 [SIGN-${signCallId}] signMessageAsync failed:`, error);
        throw error;
      }
    },
    [originalSignMessageAsync]
  );

  // Custom authentication state
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [authenticatedAddress, setAuthenticatedAddress] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [authenticationFailed, setAuthenticationFailed] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // User information state
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

  // Add a ref to prevent simultaneous authentication attempts
  const authInProgressRef = useRef(false);
  // Add a ref to track the last authentication attempt timestamp
  const lastAuthAttemptRef = useRef<number>(0);
  // Add a ref to track current connection state for timeout checks
  const isConnectedRef = useRef(isConnected);
  // Add a ref to track current embedded wallet info
  const embeddedWalletInfoRef = useRef(embeddedWalletInfo);
  // Add a ref to track if we just opened the modal (to prevent duplicate auth)
  const modalOpenedRef = useRef(false);

  useEffect(() => {
    embeddedWalletInfoRef.current = embeddedWalletInfo;
  }, [embeddedWalletInfo]);

  const { alert, showError, hideAlert } = useAlert();

  // Balance state (AppKit manages connection, we manage balances)
  const [balance, setBalance] = useState<string>("0");
  const [dxfBalance, setDxfBalance] = useState<string>("0");

  // Computed wallet state combining AppKit connection with our balance data
  const walletState = useMemo(
    (): WalletState => ({
      connected: isConnected && !!address,
      address: address || undefined,
      balance,
      dxfBalance,
      // provider and signer are managed by AppKit, not needed in our interface
    }),
    [isConnected, address, balance, dxfBalance]
  );

  // Computed authentication state - 3 conditions must be met
  const isAuthenticated = useMemo(() => {
    const authenticated = !!(
      (
        isConnected && // 1. Wallet connected (AppKit)
        jwtToken && // 2. JWT exists
        address && // 3. Address available
        authenticatedAddress && // 4. Authenticated address exists
        address.toLowerCase() === authenticatedAddress.toLowerCase() && // 5. Addresses match
        !authenticationFailed
      ) // 6. No auth failures
    );

    console.log("Authentication check:", {
      isConnected,
      hasJWT: !!jwtToken,
      hasAddress: !!address,
      hasAuthAddress: !!authenticatedAddress,
      addressesMatch:
        address && authenticatedAddress ? address.toLowerCase() === authenticatedAddress.toLowerCase() : false,
      authFailed: authenticationFailed,
      finalResult: authenticated,
    });

    return authenticated;
  }, [isConnected, jwtToken, address, authenticatedAddress, authenticationFailed]);

  // Load stored authentication data on initialization
  useEffect(() => {
    const storedToken = localStorage.getItem("deathx-auth-token");
    const storedAddress = localStorage.getItem("deathx-wallet-address");

    if (storedToken && storedAddress) {
      console.log("Loading stored auth data:", {
        storedAddress: storedAddress.slice(0, 8) + "...",
      });
      setJwtToken(storedToken);
      setAuthenticatedAddress(storedAddress);
    }

    setIsInitializing(false);
  }, []);

  // Auto-logout when addresses don't match (security feature)
  useEffect(() => {
    if (!isInitializing && jwtToken && authenticatedAddress && isConnected && address) {
      if (address.toLowerCase() !== authenticatedAddress.toLowerCase()) {
        console.log("Address mismatch detected - auto logout:", {
          connected: address.slice(0, 8) + "...",
          authenticated: authenticatedAddress.slice(0, 8) + "...",
        });
        logout(false);
        showError("Wallet Changed", "Different wallet detected. Please sign in again.");
        // login with the new wallet
        // login();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, authenticatedAddress, jwtToken, isConnected, isInitializing, showError]);


  // Perform authentication with message signing
  const performAuthentication = useCallback(
    async (walletAddress: string) => {
      // Generate unique call ID for tracking
      const callId = Math.random().toString(36).substr(2, 9);

      // Check both the in-progress flag and recent attempt timestamp
      const now = Date.now();
      const timeSinceLastAttempt = now - lastAuthAttemptRef.current;

      if (authInProgressRef.current) {
        console.log(
          `🚫 [${callId}] Authentication already in progress, skipping duplicate request (timeSinceLastAttempt: ${timeSinceLastAttempt}ms)`
        );
        return;
      }

      // Additional protection: prevent authentication attempts within 1 second of each other
      if (timeSinceLastAttempt < 1000) {
        console.log(
          `🚫 [${callId}] Authentication attempted too soon after previous attempt (${timeSinceLastAttempt}ms ago), skipping`
        );
        return;
      }

      // Set flag and timestamp immediately to prevent any race conditions
      authInProgressRef.current = true;
      lastAuthAttemptRef.current = now;
      console.log(
        `🚀 [${callId}] Authentication started for: ${walletAddress.slice(0, 8)}... (authInProgress now: ${
          authInProgressRef.current
        })`
      );

      try {
        setIsLoading(true);
        setError(null);
        setAuthenticationFailed(false);

        // Create login message
        const timestamp = Date.now();
        const message = `DeathX.Fun-Login:${walletAddress}:${timestamp}`;

        // Sign message using AppKit/Wagmi
        console.log(`📝 [${callId}] Preparing to sign message...`, {
          message,
          address,
          isConnected,
          chainId,
          timestamp,
        });

        // Additional check to ensure we have all required state
        if (!address || !isConnected) {
          throw new Error("Wallet not properly connected for signing");
        }

        // Wait a moment for AppKit connector to be fully ready
        await new Promise((resolve) => setTimeout(resolve, 100));

        let signature: string;
        try {
          console.log(`🔐 [${callId}] CALLING signMessageAsync NOW...`, {
            timestamp: Date.now(),
            message,
            address,
            isConnected,
            chainId,
            authInProgress: authInProgressRef.current,
          });

          signature = await signMessageAsync({
            message,
          });

          console.log(`✅ [${callId}] signMessageAsync completed successfully`, {
            timestamp: Date.now(),
            signatureLength: signature?.length,
            authInProgress: authInProgressRef.current,
          });
        } catch (signingError) {
          console.error("Message signing failed:", signingError);

          // If it's a connector issue, try reconnecting
          if (signingError instanceof Error && signingError.message.includes("connector")) {
            throw new Error("Wallet connection issue. Please disconnect and reconnect your wallet, then try again.");
          }

          // Re-throw other signing errors
          throw signingError;
        }

        // Get referrer code if available
        const referrerCode = getStoredReferrerCode();

        // Prepare login payload
        const loginPayload: any = {
          walletAddress,
          signature,
          message,
          timestamp,
        };

        // Add referrer code if available
        if (referrerCode) {
          loginPayload.referrerCode = referrerCode;
        }

        // Send login request to backend
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(loginPayload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Login failed");
        }

        const loginData = await response.json();

        // Store authentication data
        localStorage.setItem("deathx-auth-token", loginData.token);
        localStorage.setItem("deathx-wallet-address", loginData.walletAddress);
        setJwtToken(loginData.token);
        setAuthenticatedAddress(loginData.walletAddress);

        // Clear referrer code after successful login
        if (referrerCode) {
          clearStoredReferrerCode();
          console.log("Referrer code cleared after successful login");
        }

        console.log(`✅ [${callId}] Authentication successful`);

        // Track wallet connection event and send source data to backend
        SourceTracker.trackSourceEvent("wallet_connected");

        // Send source data to backend after successful login
        const baseSourceData = SourceTracker.getSourceDataForAPI();
        if (baseSourceData) {
          const walletInfo = embeddedWalletInfoRef.current;
          console.log(`📊 [${callId}] enhance sourceData with embeddedWalletInfo`, walletInfo);
          // Enhance source data with embedded wallet info
          const enhancedSourceData = {
            ...baseSourceData,
            walletType: walletInfo ? ("embedded" as const) : ("external" as const),
            ...(walletInfo && {
              embeddedWallet: {
                email: walletInfo.user?.email || undefined,
                username: walletInfo.user?.username || undefined,
                authProvider: walletInfo.authProvider,
                accountType: walletInfo.accountType,
              },
            }),
          };

          try {
            const result = await ApiService.sendSourceData(enhancedSourceData);
            console.log(`📊 [${callId}] Source data sent to backend successfully:`, result);
          } catch (error) {
            console.error(`📊 [${callId}] Failed to send source data to backend:`, error);
            // Don't fail the login for source tracking errors
          }
        } else {
          console.log(`📊 [${callId}] No source data to send (user may be returning visitor)`);
        }
      } catch (err) {
        console.error(`❌ [${callId}] Authentication failed:`, err);
        console.error(`❌ [${callId}] Error details:`, {
          message: err instanceof Error ? err.message : "Unknown error",
          stack: err instanceof Error ? err.stack : undefined,
          name: err instanceof Error ? err.name : undefined,
        });

        setAuthenticationFailed(true);

        const errorMessage = err instanceof Error ? err.message : "Authentication failed";
        setError(errorMessage);

        // Show user-friendly error messages
        if (err instanceof Error) {
          if (err.message.includes("User rejected") || err.message.includes("rejected")) {
            showError("Authentication Required", "Please sign the message to access your account.");
          } else if (err.message.includes("network")) {
            showError("Network Error", "Please make sure you're connected to the BSC network and try again.");
          } else if (
            err.message.includes("connector") ||
            err.message.includes("getChainId") ||
            err.message.includes("connection issue")
          ) {
            showError(
              "Wallet Connection Error",
              "There was an issue with your wallet connection. Please disconnect your wallet, refresh the page, and try connecting again."
            );
          } else {
            showError("Authentication Failed", `${err.message}`);
          }
        } else {
          showError("Authentication Failed", "An unexpected error occurred during authentication. Please try again.");
        }
      } finally {
        console.log(`🔄 [${callId}] Authentication cleanup: setting authInProgress to false`);
        authInProgressRef.current = false;
        setIsLoading(false);
      }
    },
    [address, chainId, isConnected, signMessageAsync, showError]
  );

  // Handle wallet connection changes and auto-authentication
  useEffect(() => {
    const effectCallId = Math.random().toString(36).substr(2, 9);

    // Update ref with current connection state
    isConnectedRef.current = isConnected;

    console.log(`🔄 [EFFECT-${effectCallId}] useEffect triggered - wallet state:`, {
      isInitializing,
      isLoading,
      isLoggingOut,
      isConnected,
      address: address?.slice(0, 8) + "...",
      hasJWT: !!jwtToken,
      authenticationFailed,
      authInProgress: authInProgressRef.current,
    });

    if (isInitializing || isLoading || isLoggingOut) {
      console.log("⏸️ Skipping useEffect due to state:", { isInitializing, isLoading, isLoggingOut });
      return; // Don't trigger if initializing, loading, or logging out
    }

    if (isConnected && address) {
      // Case 1: Auto-reconnection - check if JWT matches
      if (jwtToken && authenticatedAddress) {
        if (address.toLowerCase() === authenticatedAddress.toLowerCase()) {
          // Valid auto-reconnection - user is authenticated
          console.log("Valid auto-reconnection detected");
          setAuthenticationFailed(false);
          return;
        } else {
          // Address mismatch handled by separate useEffect above
          return;
        }
      }

      // Case 2: Fresh connection (no stored JWT) - auto-trigger message signing
      // But only if not already triggered, no manual authentication is in progress, and not logging out
      if (!jwtToken && !authenticationFailed && !isLoading && !isLoggingOut) {
        console.log(`🤖 [EFFECT-${effectCallId}] Fresh wallet connection - triggering automatic authentication`);

        // Add a delay to ensure AppKit connector is fully initialized
        setTimeout(async () => {
          // Double-check conditions before proceeding with auto-auth
          if (isLoading || jwtToken || authInProgressRef.current) {
            console.log(
              `🤖 [EFFECT-${effectCallId}] Skipping auto-auth: authentication already in progress or completed`,
              {
                isLoading,
                hasJWT: !!jwtToken,
                authInProgress: authInProgressRef.current,
              }
            );
            return;
          }

          try {
            console.log(
              `🤖 [EFFECT-${effectCallId}] AUTO-AUTH: Calling performAuthentication from useEffect auto-authentication`
            );
            await performAuthentication(address);
          } catch (error) {
            console.error(`🤖 [EFFECT-${effectCallId}] Auto-authentication failed:`, error);
            // Don't show error popup for auto-auth failures - user can manually retry
            setAuthenticationFailed(true);
          }
        }, 300);
      }
    } else if (!isConnected) {
      // Wallet disconnected - reset auto-auth trigger
      console.log("Wallet disconnected - resetting state");
      setAuthenticationFailed(false);

      // Only clear auth state if not in the middle of logout (to avoid recursive calls)
      if (jwtToken && !isLoggingOut) {
        // For user authenticated with social account, when refreshing page, there is a glitch that the isConnected status is false for some while then recover.
        // Not sure why, may be it's the AppKit bug. Let's do a double check
        console.log("Wallet disconnected unexpectedly - double check after 2s");
        setTimeout(() => {
          if (!isConnectedRef.current) {
            console.log("Wallet disconnected unexpectedly confirmed - clearing auth state");
            logout();
          }
        }, 2000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, address]);

  // Balance fetching using AppKit blockchain service
  const updateBalance = useCallback(async (): Promise<void> => {
    if (!address || !isAuthenticated) {
      return;
    }

    try {
      // Import here to avoid circular dependency
      const { appKitBlockchainService } = await import("../services/appkitBlockchain");

      // Fetch both BNB and DXF balances
      const [balance, dxfBalance] = await Promise.all([
        appKitBlockchainService.getBalance(address).catch((error) => {
          console.warn("Failed to update BNB balance:", error);
          return "0";
        }),
        appKitBlockchainService.getDXFBalance(address).catch((error) => {
          console.warn("Failed to update DXF balance:", error);
          return "0";
        }),
      ]);

      // Update balance state directly
      setBalance(balance);
      setDxfBalance(dxfBalance);
    } catch (error) {
      console.error("Failed to update balance:", error);
      showError("Balance Update Failed", "Could not fetch your current balance. Please try refreshing the page.");
    }
  }, [address, isAuthenticated, showError]);

  // Fetch balances when authenticated
  useEffect(() => {
    if (isConnected && address && isAuthenticated) {
      // Fetch balances when user is authenticated and connected
      updateBalance();
    } else {
      // Reset balances when not authenticated
      setBalance("0");
      setDxfBalance("0");
    }
  }, [isConnected, address, isAuthenticated, updateBalance]);

  // Comprehensive login function
  const login = useCallback(async (): Promise<void> => {
    const loginCallId = Math.random().toString(36).substr(2, 9);
    try {
      console.log(`👤 [LOGIN-${loginCallId}] Comprehensive login triggered`, {
        isConnected,
        address,
        authInProgress: authInProgressRef.current,
      });

      if (!isConnected || !address) {
        // Case 1: Wallet not connected - open AppKit modal
        console.log(`👤 [LOGIN-${loginCallId}] Opening AppKit modal for wallet connection`);
        modalOpenedRef.current = true; // Flag that we opened the modal
        open({ view: "Connect", namespace: "eip155" });
        // Note: Auto-authentication will be triggered after connection via useEffect
        // We don't need to do anything else here - the useEffect will handle authentication
        return;
      } else {
        // Case 2: Wallet already connected - perform authentication
        // Only authenticate if we don't already have a valid JWT
        if (jwtToken && authenticatedAddress && address.toLowerCase() === authenticatedAddress.toLowerCase()) {
          console.log(`👤 [LOGIN-${loginCallId}] Already authenticated with current address`);
          return;
        }

        // Check if authentication is already in progress
        if (authInProgressRef.current) {
          console.log(`👤 [LOGIN-${loginCallId}] Authentication already in progress, skipping duplicate login attempt`);
          return;
        }

        console.log(`👤 [LOGIN-${loginCallId}] Wallet connected, performing authentication`);
        // Give AppKit a moment to stabilize the connection
        await new Promise((resolve) => setTimeout(resolve, 200));
        console.log(`👤 [LOGIN-${loginCallId}] Calling performAuthentication from login function`);
        await performAuthentication(address);
      }
    } catch (error) {
      console.error(`👤 [LOGIN-${loginCallId}] Login function failed:`, error);
    }
  }, [isConnected, address, performAuthentication, open, jwtToken, authenticatedAddress]);

  // Account management functions
  const openAccount = useCallback((): void => {
    console.log("Opening Account view");
    open({ view: "Account" });
  }, [open]);

  const openOnRamp = useCallback((): void => {
    console.log("Opening OnRamp view");
    open({ view: "OnRampProviders" });
  }, [open]);

  const openSwap = useCallback((): void => {
    console.log("Opening Swap view");
    open({ view: "Swap", arguments: { toToken: "BNB" } });
  }, [open]);

  // Fetch user info from /auth/me endpoint
  const refreshUserInfo = useCallback(async (): Promise<void> => {
    if (!jwtToken) {
      setUserInfo(null);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUserInfo({
          walletAddress: userData.walletAddress,
          isAdmin: userData.isAdmin || false,
          createdAt: userData.createdAt,
          welcomeAirdropEligible: userData.welcomeAirdropEligible || false,
          welcomeAirdropParticipated: userData.welcomeAirdropParticipated || false,
        });
      } else {
        console.error('Failed to fetch user info:', response.status);
        setUserInfo(null);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      setUserInfo(null);
    }
  }, [jwtToken]);

  // Fetch user info when authenticated
  useEffect(() => {
    if (isAuthenticated && jwtToken && !isInitializing) {
      refreshUserInfo();
    }
  }, [isAuthenticated, jwtToken, isInitializing, refreshUserInfo]);

  // Logout function
  const logout = useCallback(
    (disconnectWallet?: boolean): void => {
      if (disconnectWallet === undefined) disconnectWallet = true;
      console.log("Logout called, disconnect: ", disconnectWallet);

      // Set logout flag to prevent auto-authentication
      setIsLoggingOut(true);

      // Clear authentication state
      localStorage.removeItem("deathx-auth-token");
      localStorage.removeItem("deathx-wallet-address");
      setJwtToken(null);
      setAuthenticatedAddress(null);
      setAuthenticationFailed(false);
      setError(null);
      setIsLoading(false); // Reset loading state
      setUserInfo(null); // Clear user info
      authInProgressRef.current = false; // Clear auth in progress flag

      console.log("Auth state cleared");

      if (disconnectWallet) {
        // Actually disconnect the wallet to provide clean logout experience
        disconnect();
        console.log("Wallet disconnected"); 
      }

      // Clear the logout flag after a brief delay to allow disconnect to process
      setTimeout(() => {
        setIsLoggingOut(false);
      }, 1000);
    },
    [disconnect]
  );

  const contextValue: AppKitAuthContextType = {
    isAuthenticated,
    token: jwtToken,
    walletAddress: authenticatedAddress,
    login,
    logout,
    isLoading,
    error,
    walletState,
    updateBalance,
    isInitializing,
    isEmbeddedWallet: !!embeddedWalletInfo,
    userInfo,
    refreshUserInfo,
    openAccount,
    openOnRamp,
    openSwap,
  };

  return (
    <AppKitAuthContext.Provider value={contextValue}>
      {children}
      {/* Alert Popup for Auth Errors */}
      <AlertPopup alert={alert} onClose={hideAlert} />
      {/* Note: Network switch popup removed - AppKit handles network management */}
    </AppKitAuthContext.Provider>
  );
};

export const useAppKitAuth = (): AppKitAuthContextType => {
  const context = useContext(AppKitAuthContext);
  if (context === undefined) {
    throw new Error("useAppKitAuth must be used within an AppKitAuthProvider");
  }
  return context;
};

// Export as useAuth for compatibility with existing components
export const useAuth = useAppKitAuth;
